import axios from "axios";
import { atom } from "jotai";
import { SetStateAction } from "jotai/core/types";
import { atomFamily } from "jotai/utils";

import { getTotalSweepEntries } from "../shared/utils";

import { REDIRECT_IF_PAGE_ERROR_DELAY_SECONDS } from "./cpl-offer-page.constants";
import { CplOfferPageType, CplPageType, CplType } from "./cpl-offer-page.types";
import { moveToNextPage, moveToNextPageAsync } from "./utils";

export const cplOfferPageAtom = atom<
  CplOfferPageType,
  SetStateAction<CplOfferPageType>
>(
  async () => {
    try {
      const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
      const userEmail = submission?.data?.email;
      const { data }: { data: CplOfferPageType } = await axios.get(
        `${
          window.FKL.yentaServicesApiUrl
        }/cpl-grid/${window.FKL.sweepstakeId.trim()}/${
          userEmail ? encodeURIComponent(userEmail) : "no-email"
        }`
      );

      if (!data.pages || data.pages.length === 0) {
        await moveToNextPageAsync();
      }

      try {
        if (submission) {
          await window.theme.Sweepstake.prototype.checkCplExtraEntriesHash();
        }
        data.allowedPage = true;
      } catch (err) {
        data.allowedPage = false;
        setTimeout(() => {
          moveToNextPage();
        }, REDIRECT_IF_PAGE_ERROR_DELAY_SECONDS * 1000);
      }

      return {
        ...data,
        pages: data.pages.map((cplPage) =>
          cplPage.map((cpl) => ({
            ...cpl,
            isAccepted: false,
            gaImpressionSent: false,
          }))
        ),
        initialCplExtraEntries: submission?.data?.cplExtraEntries ?? 0,
        initialAcceptedCpls: submission?.data?.quizAcceptedCpls ?? "",
        sessionCplExtraEntries: 0,
        allTimeEntries: getTotalSweepEntries(submission?.data, [
          "cplExtraEntries",
        ]),
        emailReceiver: userEmail || "",
      } as CplOfferPageType;
    } catch (error) {
      if (
        error.response?.data?.message ===
        `CPL for Sweep with id=${window.FKL.sweepstakeId.trim()} is not GRID type`
      ) {
        await moveToNextPageAsync();
      }
      throw new Error(error.response?.data?.message ?? error.message);
    }
  },
  (get, set, update) => {
    const newValue =
      typeof update === "function" ? update(get(cplOfferPageAtom)) : update;
    set(cplOfferPageAtom, newValue);
  }
);

export const currentCplPageIndexAtom = atom<number>(0);

export const currentCplPageAtom = atom<CplPageType>((get) => {
  const { minimumCplOffersTaken } = window.FKL;
  const cplPage = get(cplOfferPageAtom).pages[get(currentCplPageIndexAtom)];
  const calculatedMinimumCplOffersTaken =
    minimumCplOffersTaken > cplPage.length ? 1 : minimumCplOffersTaken;

  if (!cplPage || cplPage.length === 0) {
    moveToNextPage();
  }

  return {
    minimumCplOffersTaken: calculatedMinimumCplOffersTaken,
    cpls: cplPage,
  } as CplPageType;
});

export const CplAtom = atomFamily<string, CplType, SetStateAction<CplType>>(
  (id) =>
    atom(
      (get) => {
        const cpls = (get(cplOfferPageAtom)?.pages ?? []).reduce(
          (acc, cplPage) => [...acc, ...cplPage],
          []
        );
        const cpl = cpls.find((cpl) => cpl.id === id);

        if (!cpl) {
          throw new Error(`CPL id: ${id} does not exist`);
        }

        return cpl;
      },
      (_get, set, update) => {
        set(cplOfferPageAtom, (cplOfferPage) => ({
          ...cplOfferPage,
          pages: cplOfferPage.pages.map((cplPage) =>
            cplPage.map((cpl) =>
              cpl.id !== id
                ? cpl
                : typeof update === "function"
                ? update(cpl)
                : update
            )
          ),
        }));
      }
    )
);
